/**
 * Combat Stage 2: advanced enemy AI — archetypes, evasion/jink, obstacle
 * avoidance, flanking — layered on top of the G1 rigidbody foundation.
 * Pure, deterministic — no Three.js / DOM / Math.random / Date.now.
 * All position/velocity units are km and km/s (sim-core convention).
 *
 * Coordinate convention (confirmed from rigidbody.ts + shipController.ts):
 *   body frame: -Z is forward
 *   commandTorque axes: cmd.x = pitch, cmd.y = yaw, cmd.z = roll
 *   rotV(q, v): body → world.  rotV(conjQ(q), v): world → body.
 *
 * BACKWARD-COMPAT: 4-arg call (no env) is identical to G1. All 12 existing
 * tests pass unchanged. The optional 5th env arg activates Stage 2 behavior.
 * npc.id is optional; absent id → G1 path (preserves minimal test fixtures).
 */
import { add, sub, scale, normalize, length, dot, vec3, type Vec3 } from './vec3.js';
import { type Quat } from './quat.js';
import {
  makeRigidBody,
  integrate,
  commandTorque,
  type RigidBody,
  type AngularDrive,
} from './rigidbody.js';
import { shipById, type ShipSpec } from './ship.js';
import {
  aimDirection,
  BOLT_SPEED_KMS,
  NPC_FIRE_RANGE_KM,
} from './combat.js';
import { type NpcShip } from './npc.js';

// ── helpers ──────────────────────────────────────────────────────────────────

const conjQ = (q: Quat): Quat => ({ w: q.w, x: -q.x, y: -q.y, z: -q.z });

const rotV = (q: Quat, v: Vec3): Vec3 => {
  const { w, x, y, z } = q;
  const vx = v.x, vy = v.y, vz = v.z;
  const tx = 2 * (y * vz - z * vy);
  const ty = 2 * (z * vx - x * vz);
  const tz = 2 * (x * vy - y * vx);
  return {
    x: vx + w * tx + (y * tz - z * ty),
    y: vy + w * ty + (z * tx - x * tz),
    z: vz + w * tz + (x * ty - y * tx),
  };
};

const clampAbs = (v: number, lim: number): number => Math.max(-lim, Math.min(lim, v));
const clampV   = (v: Vec3): Vec3 => vec3(clampAbs(v.x, 1), clampAbs(v.y, 1), clampAbs(v.z, 1));
const blendV   = (a: Vec3, b: Vec3, t: number): Vec3 => add(scale(a, 1 - t), scale(b, t));

// ── deterministic hash ───────────────────────────────────────────────────────

const hash = (seed: number, i: number): number =>
  ((Math.imul(seed >>> 0, 2654435761) ^ Math.imul(i >>> 0, 40503)) >>> 0);

const idToSeed = (id: string): number => {
  const digits = Number(id.replace(/\D/g, ''));
  if (!isNaN(digits) && digits > 0) return digits;
  let h = 5381;
  for (let i = 0; i < id.length; i++) h = (Math.imul(h, 33) ^ id.charCodeAt(i)) >>> 0;
  return h;
};

// ── archetypes ───────────────────────────────────────────────────────────────

export type NpcKind = 'rusher' | 'sniper' | 'flanker';

export interface NpcProfile {
  kind: NpcKind;
  standoffFar: number;
  standoffNear: number;
  aggression: number;
  jinkAmp: number;
  jinkPhase: number;
  jinkFreq: number;
}

/**
 * Derive a deterministic NpcProfile from a stable NPC id string.
 *   hash%3==0 → rusher:  tight band 15–40 km, high aggression, high jink
 *   hash%3==1 → sniper:  long band  60–88 km, low  aggression, low  jink
 *   hash%3==2 → flanker: mid  band  30–60 km, med  aggression, med  jink
 */
export const deriveProfile = (id: string): NpcProfile => {
  const seed = idToSeed(id);
  const kindIndex = hash(seed, 0) % 3;
  const phase = (hash(seed, 1) % 1000) / 1000 * Math.PI * 2;
  const freqBase = 0.8 + (hash(seed, 2) % 500) / 1000;
  if (kindIndex === 0) {
    return { kind: 'rusher',  standoffFar: 40, standoffNear: 15,
      aggression: 0.85 + (hash(seed, 3) % 100) / 667,
      jinkAmp:    0.50 + (hash(seed, 4) % 100) / 200,
      jinkPhase: phase, jinkFreq: freqBase * 1.4 };
  }
  if (kindIndex === 1) {
    return { kind: 'sniper',  standoffFar: 88, standoffNear: 60,
      aggression: 0.25 + (hash(seed, 3) % 100) / 400,
      jinkAmp:    0.12 + (hash(seed, 4) % 100) / 833,
      jinkPhase: phase, jinkFreq: freqBase * 0.6 };
  }
  return { kind: 'flanker', standoffFar: 60, standoffNear: 30,
    aggression: 0.55 + (hash(seed, 3) % 100) / 500,
    jinkAmp:    0.30 + (hash(seed, 4) % 100) / 333,
    jinkPhase: phase, jinkFreq: freqBase };
};

// ── jink ─────────────────────────────────────────────────────────────────────

interface JinkResult { thrustX: number; thrustY: number; rollCmd: number }

/**
 * Sinusoidal evasion in body X/Y + roll. Two quadrature oscillators give a
 * Lissajous weave. Amplitude kept ≤ 0.6 so the fire gate can still close.
 */
const computeJink = (p: NpcProfile, t: number): JinkResult => ({
  thrustX: clampAbs(Math.sin(p.jinkFreq * t + p.jinkPhase) * p.jinkAmp, 0.6),
  thrustY: clampAbs(Math.cos(p.jinkFreq * t * 0.7 + p.jinkPhase + 1.1) * p.jinkAmp * 0.6, 0.4),
  rollCmd: clampAbs(Math.sin(p.jinkFreq * t * 0.5 + p.jinkPhase + Math.PI / 2) * p.jinkAmp * 0.5, 0.5),
});

// ── obstacle avoidance ────────────────────────────────────────────────────────

const AVOIDANCE_MARGIN_KM = 5;

interface AvoidResult { weight: number; thrust: Vec3; rotCommand: Vec3 }

/** Smoothstep avoidance when a body is within radiusKm*1.5 + margin. */
const computeObstacleAvoidance = (
  npcPos: Vec3, _npcVel: Vec3, orient: Quat,
  body: { pos: Vec3; radiusKm: number },
): AvoidResult => {
  const toBody = sub(body.pos, npcPos);
  const cd = length(toBody);
  const trigger = body.radiusKm * 1.5 + AVOIDANCE_MARGIN_KM;
  if (cd >= trigger) return { weight: 0, thrust: vec3(), rotCommand: vec3() };
  const raw = 1 - (cd - body.radiusKm) / (trigger - body.radiusKm);
  const w = Math.min(1, Math.max(0, raw * raw * (3 - 2 * raw)));
  const awayBody = rotV(conjQ(orient), normalize(scale(toBody, -1)));
  const obstBody = rotV(conjQ(orient), normalize(toBody));
  return {
    weight: w,
    thrust: clampV(vec3(awayBody.x, awayBody.y, awayBody.z - 0.8)),
    rotCommand: vec3(clampAbs(-obstBody.y * 3, 1), clampAbs(-obstBody.x * 3, 1), 0),
  };
};

// ── flanking ─────────────────────────────────────────────────────────────────

const computeFlankPoint = (npcPos: Vec3, playerPos: Vec3, p: NpcProfile): Vec3 => {
  const toPlayer = sub(playerPos, npcPos);
  const dist = length(toPlayer);
  if (dist < 1e-6) return playerPos;
  const fwd = normalize(toPlayer);
  const cos = Math.cos(p.jinkPhase), sin = Math.sin(p.jinkPhase);
  const ax = normalize({ x: cos, y: sin, z: cos * 0.3 });
  const px = fwd.y * ax.z - fwd.z * ax.y;
  const py = fwd.z * ax.x - fwd.x * ax.z;
  const pz = fwd.x * ax.y - fwd.y * ax.x;
  const pl = Math.sqrt(px * px + py * py + pz * pz);
  if (pl < 1e-6) return playerPos;
  const perp: Vec3 = { x: px / pl, y: py / pl, z: pz / pl };
  return add(playerPos, scale(perp, Math.min(dist * 0.4, 25)));
};

const computeFlankThrust = (
  npc: { pos: Vec3; vel: Vec3; orientation: Quat },
  player: { pos: Vec3; vel: Vec3 },
  p: NpcProfile, thrustZ: number,
): Vec3 => {
  const fp = computeFlankPoint(npc.pos, player.pos, p);
  const toFlank = sub(fp, npc.pos);
  if (length(toFlank) < 1e-6) return vec3(0, 0, thrustZ);
  const tb = rotV(conjQ(npc.orientation), normalize(toFlank));
  return vec3(clampAbs(tb.x * 0.4, 0.5), clampAbs(tb.y * 0.12, 0.2), thrustZ);
};

// ── fire gate helper ──────────────────────────────────────────────────────────

const SNIPER_FIRE_DOT  = 0.985;
const GENERIC_FIRE_DOT = 0.96;
const RUSHER_FIRE_DOT  = 0.94;

const fireDotFor = (k: NpcKind): number =>
  k === 'sniper' ? SNIPER_FIRE_DOT : k === 'rusher' ? RUSHER_FIRE_DOT : GENERIC_FIRE_DOT;

const evalFireGate = (
  facing: Vec3, lead: Vec3, dist: number, cd: number,
  p: NpcProfile, avoidW: number,
): boolean => {
  const aligned = dot(facing, normalize(lead)) > fireDotFor(p.kind);
  const inRange = dist <= NPC_FIRE_RANGE_KM && dist > 1e-6;
  const inBand  = p.kind === 'sniper' ? dist >= p.standoffNear : true;
  return cd <= 0 && inRange && inBand && aligned && avoidW < 0.7;
};

// ── Stage 3: separation, role split, morale ───────────────────────────────────

const MIN_SPACING_KM = 12;
const MORALE_BREAK_FRAC = 0.3;
const BREAK_RANGE_KM = NPC_FIRE_RANGE_KM * 1.5; // re-engage beyond this km

/**
 * Anti-clump separation: if the nearest ally is within MIN_SPACING_KM, add a
 * lateral steer-away thrust in body frame.  Blended UNDER the pursuit vector
 * so ships still engage while spreading out.
 * Returns a body-frame additive thrust (x/y only; z unchanged) plus a blend weight.
 */
const computeSeparation = (
  npc: { pos: Vec3; orientation: Quat },
  allies: { id: string; pos: Vec3 }[],
): { sepThrust: Vec3; sepWeight: number } => {
  if (allies.length === 0) return { sepThrust: vec3(), sepWeight: 0 };
  let nearest: Vec3 | null = null;
  let nearestDist = Infinity;
  for (const a of allies) {
    const d = length(sub(a.pos, npc.pos));
    if (d < nearestDist) { nearestDist = d; nearest = a.pos; }
  }
  if (nearest === null || nearestDist >= MIN_SPACING_KM) return { sepThrust: vec3(), sepWeight: 0 };
  const raw = 1 - nearestDist / MIN_SPACING_KM;
  const w = Math.min(1, raw * raw * (3 - 2 * raw)); // smoothstep
  const awayWorld = normalize(sub(npc.pos, nearest));
  const awayBody  = rotV(conjQ(npc.orientation), awayWorld);
  // Only lateral component — don't fight the forward pursuit, just push sideways.
  return {
    sepThrust: vec3(clampAbs(awayBody.x * 1.2, 1), clampAbs(awayBody.y * 1.2, 1), 0),
    sepWeight: w * 0.55,  // max 55 % blend so the wing still engages
  };
};

/**
 * Role split: derive a deterministic unit-vector maneuver offset per NPC id so
 * different ids in a wing attack from different vectors.  Used as an additive
 * lateral bias to the pursuit thrust.  Two distinct ids produce different vectors.
 */
const computeRoleOffset = (id: string): Vec3 => {
  const seed = idToSeed(id);
  const angle = (hash(seed, 7) % 1000) / 1000 * Math.PI * 2; // 0..2π unique per id
  // Body-frame lateral preference (x/y only; forward/back from pursuit).
  return vec3(Math.cos(angle) * 0.35, Math.sin(angle) * 0.25, 0);
};

/**
 * Morale / break-off: when hull is critical (< MORALE_BREAK_FRAC), the ship
 * flees AWAY from the player at full burn and stops firing.  Once it retreats
 * beyond BREAK_RANGE_KM it may re-engage (but hull check repeats each frame).
 * Pure — deterministic based on selfHullFrac and dist.
 */
const computeBreakOff = (
  npc: { pos: Vec3; orientation: Quat },
  player: { pos: Vec3 },
  selfHullFrac: number,
  dist: number,
): NpcCombatCommand | null => {
  if (selfHullFrac >= MORALE_BREAK_FRAC) return null;
  // Beyond break range: resume normal maneuver (hull is still low but they re-engage).
  if (dist > BREAK_RANGE_KM) return null;
  // Thrust AWAY from player (world away vector → body frame → push -Z = fwd = away).
  const awayWorld  = normalize(sub(npc.pos, player.pos));
  const awayBody   = rotV(conjQ(npc.orientation), awayWorld);
  // We want to fly in the direction of awayWorld, so point nose there (-Z body = fwd):
  // rotate toward the escape direction then thrust forward (-Z).
  // AUDIT FIX (session 6): turn-toward-direction servo signs. For body frame
  // -Z forward / +X right / +Y up: +x angular cmd pitches the nose UP, +y yaws
  // the nose LEFT. So pointing the nose at body-frame dir d needs
  // (+d.y*K, -d.x*K). The pitch term here was inverted (fled nose-down spirals).
  const K = 4;
  const rotCmd = vec3(
    clampAbs(awayBody.y * K, 1),
    clampAbs(-awayBody.x * K, 1),
    0,
  );
  return {
    thrust:     vec3(0, 0, -1),    // full burn forward (nose will turn away)
    rotCommand: rotCmd,
    shouldFire: false,             // disengage — no fire during break-off
  };
};

// ── Stage 2 inner ────────────────────────────────────────────────────────────

const computeStage2Thrust = (
  npc: { pos: Vec3; vel: Vec3; orientation: Quat; id: string },
  player: { pos: Vec3; vel: Vec3 },
  p: NpcProfile, dist: number,
): Vec3 => {
  const thrustZ = dist > p.standoffFar ? -1 : dist < p.standoffNear ? 0.5 : 0;
  if (p.kind === 'flanker' || (p.kind === 'rusher' && dist > p.standoffNear * 1.5)) {
    return computeFlankThrust(npc, player, p, thrustZ);
  }
  return vec3(0, 0, thrustZ);
};

const computeStage2Command = (
  npc: { pos: Vec3; vel: Vec3; orientation: Quat; id: string },
  player: { pos: Vec3; vel: Vec3 },
  facing: Vec3, baseRot: Vec3, dist: number,
  cd: number, env: NpcCombatEnv,
): NpcCombatCommand => {
  // Stage 3 morale: check break-off FIRST — overrides everything.
  const hullFrac = env.selfHullFrac ?? 1;
  const breakOff = computeBreakOff(npc, player, hullFrac, dist);
  if (breakOff !== null) return breakOff;

  const p = deriveProfile(npc.id);
  const t = env.simTime ?? 0;
  const nb = env.nearestBody ?? null;
  const av = nb ? computeObstacleAvoidance(npc.pos, npc.vel, npc.orientation, nb)
                : { weight: 0, thrust: vec3(), rotCommand: vec3() };
  const pursuitT = computeStage2Thrust(npc, player, p, dist);
  const engaged  = dist <= NPC_FIRE_RANGE_KM * 1.1 && p.jinkAmp > 0;
  const jink     = engaged ? computeJink(p, t) : { thrustX: 0, thrustY: 0, rollCmd: 0 };

  // Stage 3 role split: deterministic per-id lateral bias so wing members attack
  // from different vectors.  Added alongside jink before separation/avoidance.
  const role = computeRoleOffset(npc.id);
  const withJink: Vec3 = {
    x: pursuitT.x + jink.thrustX + role.x,
    y: pursuitT.y + jink.thrustY + role.y,
    z: pursuitT.z,
  };

  // Stage 3 separation: blend away-from-ally lateral term on top of pursuit.
  const allies = env.allies ?? [];
  const { sepThrust, sepWeight } = computeSeparation(npc, allies);
  const withSep = sepWeight > 0 ? blendV(withJink, add(withJink, sepThrust), sepWeight) : withJink;

  const afterAvoid = blendV(withSep, av.thrust, av.weight);
  const rotWRoll   = vec3(baseRot.x, baseRot.y, jink.rollCmd);
  const finalR     = clampV(blendV(rotWRoll, av.rotCommand, av.weight));
  const lead       = aimDirection(npc.pos, player.pos, player.vel, BOLT_SPEED_KMS);
  return {
    thrust:     clampV(afterAvoid),
    rotCommand: finalR,
    shouldFire: evalFireGate(facing, lead, dist, cd, p, av.weight),
  };
};

// ── public API ───────────────────────────────────────────────────────────────

export const makeCombatBody = (
  shipType: string, pos: Vec3, vel: Vec3 = vec3(),
): RigidBody => {
  const spec = shipById(shipType);
  return makeRigidBody({
    position: { ...pos }, velocity: { ...vel },
    mass: spec.massKg, inertia: spec.inertia,
    linearDamping: spec.linearDamping, angularDamping: spec.angularDamping,
  });
};

export const driveFromSpec = (spec: ShipSpec): AngularDrive => ({
  maxRate: vec3(spec.keyTurnRate, spec.keyTurnRate, spec.rollRate),
  accel:   vec3(spec.pitchYawAccel, spec.pitchYawAccel, spec.rollAccel),
  maxTorque: vec3(
    spec.inertia.ix * spec.maxAngAccel,
    spec.inertia.iy * spec.maxAngAccel,
    spec.inertia.iz * spec.maxAngAccel,
  ),
});

export interface NpcCombatCommand {
  thrust: Vec3;
  rotCommand: Vec3;
  shouldFire: boolean;
}

export interface NpcCombatEnv {
  simTime?: number;
  nearestBody?: { pos: Vec3; radiusKm: number } | null;
  /** Other living hostiles in the same wing (excludes self + victims). */
  allies?: { id: string; pos: Vec3 }[];
  /** Self hull health fraction 0..1. When < MORALE_BREAK_FRAC, triggers break-off. */
  selfHullFrac?: number;
}

/**
 * Pure AI command.  4-arg call (no env, no npc.id) = G1 identical.
 * 5-arg call with env + npc.id activates Stage 2: archetypes, jink,
 * obstacle avoidance, flanking.
 */
export const computeNpcCombatCommand = (
  npc: { pos: Vec3; vel: Vec3; orientation: Quat; id?: string },
  player: { pos: Vec3; vel: Vec3 },
  shipType: string,
  cooldownLeft: number,
  env?: NpcCombatEnv,
): NpcCombatCommand => {
  const lead   = aimDirection(npc.pos, player.pos, player.vel, BOLT_SPEED_KMS);
  const facing = rotV(npc.orientation, vec3(0, 0, -1));
  const bodyLead = rotV(conjQ(npc.orientation), lead);
  // AUDIT FIX (session 6): yaw sign was INVERTED (+bodyLead.x steered the nose
  // AWAY from the lead point — positive feedback). With body -Z forward, +y
  // angular cmd = nose LEFT, so aiming at body-frame dir d needs y = -d.x*K.
  // Live symptom: hostile noses orbited chaotically (aimDot swept -0.6..0.7),
  // the fire gate never opened, and return fire NEVER happened in integration.
  const K = 4;
  const baseRot = vec3(clampAbs(bodyLead.y * K, 1), clampAbs(-bodyLead.x * K, 1), 0);
  const dist = length(sub(player.pos, npc.pos));
  if (env !== undefined && npc.id !== undefined) {
    return computeStage2Command(
      npc as { pos: Vec3; vel: Vec3; orientation: Quat; id: string },
      player, facing, baseRot, dist, cooldownLeft, env,
    );
  }
  // G1 fallback — unchanged
  const thrustZ = dist > 70 ? -1 : dist < 30 ? 0.5 : 0;
  const aligned = dot(facing, normalize(lead)) > GENERIC_FIRE_DOT;
  const inRange = dist <= NPC_FIRE_RANGE_KM && dist > 1e-6;
  return {
    thrust: vec3(0, 0, thrustZ),
    rotCommand: baseRot,
    shouldFire: cooldownLeft <= 0 && inRange && aligned,
  };
};

export const stepNpcCombatPhysics = (
  npc: NpcShip, body: RigidBody,
  command: NpcCombatCommand, dt: number,
): { npc: NpcShip; body: RigidBody } => {
  const spec  = shipById(npc.shipType);
  const drive = driveFromSpec(spec);
  const worldThrust = rotV(body.orientation, command.thrust);
  const force  = scale(worldThrust, spec.thrust);
  const torque = commandTorque(body, command.rotCommand, drive);
  const body2  = integrate(body, force, torque, dt, true);
  return {
    npc: { ...npc, pos: body2.position, vel: body2.velocity, orientation: body2.orientation },
    body: body2,
  };
};
